import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { getPublicAgents, type PublicAgent } from '../../services/agentService';
import { TYPE } from '../../constants/typeScale';
import CastingCarousel from './CastingCarousel';
import CandidateBench, { AUDITION_PANEL_ID } from './CandidateBench';

/**
 * AGENT HIRING MODAL — the one way an AI agent is ever picked (owner law, 2026-08-15).
 *
 * "Just the name without visuals and details doesn't feel like a hiring process." Choosing an agent is
 * HIRING: you should meet the candidate — portrait at size, their social gallery, the full bio — before you
 * put them on your production. So every agent-selection surface opens THIS modal rather than offering a row of
 * name chips. `CastingCarousel` auditions ONE candidate; this modal owns the candidate LIST.
 *
 * Owning the list means owning how a creator moves through it, and that used to be a pair of chevrons that
 * looked exactly like the chevrons paging one candidate's photos (owner 2026-08-23: neither was obvious). The
 * dialog now has two clearly different KINDS of control: the photos live on the picture, and the people live
 * on a BENCH of faces underneath — everyone who could take this seat, visible, one click each. A hiring room
 * shows you the candidates; it does not deal them one at a time face-down.
 *
 * Candidates arrive as agent ids (each surface knows its own bench — writers, directors, composers, cast);
 * the modal resolves them to full profiles itself so callers stay one-liner simple.
 */
export interface AgentHiringModalProps {
  open: boolean;
  onClose: () => void;
  /** The bench to show, in display order. */
  candidateIds: string[];
  /** Who currently holds the seat, if anyone — the audition opens on them. */
  currentId?: string | null;
  /** What the seat is, for the title: "Choose your writer". */
  title: string;
  /** The hire verb on the action button: "Hire" / "Cast". */
  verb?: string;
  /** The already-hired state label: "Your writer" / "In your cast". */
  hiredLabel?: string;
  /** Per-candidate craft note (agentId -> "Kinetic short-form · Fast, punchy cutting") — the catalog knows what
   *  a candidate brings to THIS seat, which their public bio cannot. Shown beside the portrait. */
  notes?: Record<string, string>;
  onHire: (agent: PublicAgent) => void;
}

const AgentHiringModal: React.FC<AgentHiringModalProps> = ({
  open, onClose, candidateIds, currentId, title, verb = 'Recruit', hiredLabel = 'On your team', notes, onHire,
}) => {
  const [roster, setRoster] = useState<PublicAgent[] | null>(null);
  const [idx, setIdx] = useState(0);

  // The public roster is small and cached by the browser; resolve ids -> profiles once per open.
  useEffect(() => {
    if (!open) return;
    let alive = true;
    getPublicAgents()
      .then((all) => { if (alive) setRoster(all); })
      .catch(() => { if (alive) setRoster([]); });
    return () => { alive = false; };
  }, [open]);

  const candidates = useMemo(() => {
    if (!roster) return null;
    const byId = new Map(roster.map((a) => [a.id, a]));
    return candidateIds.map((id) => byId.get(id)).filter((a): a is PublicAgent => !!a);
  }, [roster, candidateIds]);

  // Open on the current seat-holder, so "who do I have now?" is the first thing seen.
  useEffect(() => {
    if (!open || !candidates?.length) return;
    const at = currentId ? candidates.findIndex((a) => a.id === currentId) : 0;
    setIdx(at >= 0 ? at : 0);
  }, [open, candidates, currentId]);

  /**
   * ESCAPE LEAVES THE HIRING ROOM — and it has to work without the creator clicking inside first.
   *
   * Found by the creator journey 2026-08-29: this handler existed and did nothing. React's `onKeyDown` fires
   * only for events that pass through this subtree, and opening the modal left focus on the SEAT BUTTON
   * behind the overlay — so the keypress never reached the dialog and the modal could only be dismissed with
   * a mouse. The same root cause is the worse bug: focus stayed outside, so Tab walked the page UNDERNEATH an
   * `aria-modal` overlay, which is a keyboard trap in the exact sense the attribute promises it is not.
   *
   * So the dialog takes focus when it opens, and the listener goes on the DOCUMENT: whatever is focused
   * inside it — a card, the close button, a scroll container — Escape means the same thing.
   */
  const dialogRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    dialogRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  const agent = candidates?.[Math.min(idx, Math.max((candidates?.length || 1) - 1, 0))];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-testid="hiring-modal"
      ref={dialogRef}
      // Focusable so the dialog itself can hold focus on open; -1 keeps it out of the tab ORDER, since it is a
      // container rather than a control.
      tabIndex={-1}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* The room never grows past the window: a short screen scrolls the audition, it does not push the
          title and the way out off the top. */}
      <div className="flex max-h-[calc(100vh-2rem)] w-full max-w-2xl flex-col rounded-2xl border border-secondary-200 bg-white shadow-2xl dark:border-secondary-700 dark:bg-secondary-900">
        <div className="flex shrink-0 items-center justify-between border-b border-secondary-200 px-4 py-3 dark:border-secondary-800">
          <h2 className={`${TYPE.sectionTitle} text-secondary-900 dark:text-white`}>{title}</h2>
          <button type="button" onClick={onClose} aria-label="Close"
            className="p-1 text-secondary-400 transition-colors hover:text-secondary-700 dark:hover:text-white">
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        <div className="overflow-y-auto p-4">
          {!candidates ? (
            <div className={`${TYPE.body} flex min-h-[300px] items-center justify-center gap-2 text-secondary-400`}>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Meeting the candidates…
            </div>
          ) : !agent ? (
            <p className={`${TYPE.body} min-h-[300px] py-10 text-center text-secondary-500 dark:text-secondary-400`}>
              Nobody is available for this seat right now.
            </p>
          ) : (
            <>
              <div id={AUDITION_PANEL_ID} role="tabpanel" aria-label={`${agent.name}, auditioning`}>
                <CastingCarousel
                  agent={agent}
                  note={notes?.[agent.id]}
                  alreadyCast={!!currentId && agent.id === currentId}
                  verb={verb}
                  hiredLabel={hiredLabel}
                  onHire={(a) => { onHire(a); onClose(); }}
                />
              </div>
              <CandidateBench candidates={candidates} index={idx} onPick={setIdx} currentId={currentId} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgentHiringModal;
